import { DocContent } from '../docsData';

const doc: DocContent = {
  title: 'Slack',
  content: `
A Slack connection is one Slack bot your agents work through. It can do two things:

- **Answer messages.** Mention the bot in a channel (\`@Dougie what was revenue last week?\`) or send it a direct message, and the agent that listens to it replies in the thread. Each thread is one piece of work, so follow-ups in the same thread stay together.
- **Ask for approval.** When a step in an agent needs a person's OK, the bot posts what the agent wants to do with three buttons:
  - **Approve**: the next step runs with exactly what was approved.
  - **Approve with changes**: you type what should change (for example "make it high priority"), and the next step runs with your changes.
  - **Reject**: nothing more happens.

  Only an **Admin** of your Pliable account can decide. Pliable matches the person who clicked to a Pliable user by their Slack email address.

**One Slack app per bot.** Slack gives each app exactly one bot, so if you want two bots with their own names (say "Dougie" for data questions and "Liam" for approvals), create two Slack apps and add a connection for each. An agent replies only to messages that came to its own bot.

---

## 1. Create the Slack app

1. Go to [api.slack.com/apps](https://api.slack.com/apps) and click **Create New App**, then **From a manifest**.
2. Pick your workspace, choose **JSON**, and paste (change the name to your bot's):

\`\`\`json
{
  "display_information": { "name": "Dougie" },
  "features": {
    "bot_user": { "display_name": "Dougie", "always_online": true },
    "app_home": { "messages_tab_enabled": true, "messages_tab_read_only_enabled": false }
  },
  "oauth_config": {
    "scopes": { "bot": ["chat:write", "users:read", "users:read.email", "app_mentions:read", "im:history", "reactions:write"] }
  }
}
\`\`\`

3. Click **Create**. If your workspace requires admin approval for new apps, request it.

The permissions let the bot post (\`chat:write\`), see who clicked an approval button (\`users:read\`, \`users:read.email\`), receive mentions and direct messages (\`app_mentions:read\`, \`im:history\`), and react with 👀 where Slack can't show "Dougie is working…" (\`reactions:write\`).

---

## 2. Copy its two secrets

- **Signing secret:** on the app's **Basic Information** page, under **App Credentials**, click **Show** next to **Signing Secret**.
- **Bot token:** open **OAuth & Permissions**, click **Install to Workspace**, then **Allow**. Copy the **Bot User OAuth Token**, which starts with \`xoxb-\`.

You don't need an app-level token (\`xapp-\`). If Slack gave you one, the app has **Socket Mode** on. Turn it off under **Settings → Socket Mode**: with it on, Slack never sends anything to Pliable.

---

## 3. Add the connection in Pliable

1. Open the **Agents** page and find **Connections**.
2. Choose **Slack**, give the connection a name (for example \`dougie-slack\`), and paste the bot token and signing secret.
3. Click **Add connection**.

Pliable stores the secrets encrypted and never shows them again. To change one later, use **Replace secret**.

The new connection shows a **delivery URL**. Copy it: the next two steps both use it.

---

## 4. Send messages to Pliable

For a bot that answers messages:

1. In your Slack app, open **Event Subscriptions** and switch it **On**.
2. Paste the delivery URL as the **Request URL**. Slack checks it right away and shows **Verified**.
3. Under **Subscribe to bot events**, add \`app_mention\` and \`message.im\`.
4. Click **Save Changes**. If Slack asks you to reinstall the app, do: it can issue a new bot token, which goes in through **Replace secret**.

---

## 5. Send button clicks to Pliable

For a bot that asks for approval:

1. Open **Interactivity & Shortcuts** and switch it **On**.
2. Paste the same delivery URL as the **Request URL** and click **Save Changes**.

---

## 6. Invite the bot to a channel

1. In each channel where the bot should answer or ask, type \`/invite @Dougie\`.
2. For an approval gate, find the channel's ID: click the channel name, open the **About** tab, and copy the **Channel ID** at the bottom (it starts with \`C\`).

---

## 7. Use it in an agent

To answer messages, give the agent a trigger on the connection and the reply capability:

\`\`\`json
"triggers": [{ "connection": "dougie-slack", "event": "message.received" }],
"capabilities": [
  { "connection": "pliable", "capability": "ellie.ask", "params": {} },
  { "connection": "dougie-slack", "capability": "message.reply", "params": {} }
]
\`\`\`

\`ellie.ask\` lets the agent ask Ellie about your data.

To post to a channel instead (a daily summary, a triage result), use \`message.post\` with the channel's ID. The agent writes the message; it can't choose another channel:

\`\`\`json
{ "connection": "dougie-slack", "capability": "message.post", "params": { "channel": "C0123456789" } }
\`\`\`

> **Who sees what:** an agent answers with **its own** access, not the access of the person asking. Anyone who can message the bot can ask anything the agent can see. Invite it only to channels whose members may see that data, or limit what the agent can reach.

To ask for approval, put a gate on the step that needs it, naming the connection and the channel:

\`\`\`json
{
  "from": "alert-triager",
  "when": { "action": "propose" },
  "gate": { "connection": "liam-slack", "channel": "C0123456789" },
  "to": "ticket-writer"
}
\`\`\`

---

## Troubleshooting

| What you see | What to do |
|---|---|
| The bot doesn't answer a mention | Check **Event Subscriptions** is on with the delivery URL, and subscribed to \`app_mention\`; the bot must be in the channel. |
| You can't message the bot directly | In **App Home**, turn on **Messages Tab** and allow users to send messages, then reinstall the app. |
| Clicking a button replies "Only an Admin of this Pliable workspace can decide this." | The clicker's Slack email isn't an Admin in Pliable. Use the same email in both, and check their role. |
| No approval message appears in the channel | Invite the bot to the channel (step 6), and check the channel ID in the gate. |
| Buttons do nothing, or Slack shows an error | Check that the Request URL in **Interactivity & Shortcuts** is the connection's delivery URL, and that you clicked **Save Changes**. |
| There's no Request URL field under **Interactivity & Shortcuts** | Socket Mode is on. Turn it off under **Settings → Socket Mode**. |
| It stopped working after you changed the app's permissions | Slack asked you to reinstall the app, which can issue a new bot token. Copy it from **OAuth & Permissions** and use **Replace secret** on the connection. |
`,
};

export default doc;
